import { forwardRef, useEffect, useRef, type ComponentPropsWithoutRef } from 'react';
import { intersection, pick, union } from 'lodash-es';
import { VariableSizeList } from 'react-window';
import type { KeysOfUnion } from 'type-fest';

import { useAppSelector } from '@/hooks/useAppSelector';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { isEmptyOrUndefined } from '@/utilities/string';

import { Fields, SecretListId } from '../../constants';
import { useSecretsListOffset, useSharingIndicatorVisibility } from '../../hooks';
import { selectFilteredSecrets } from '../../services/selectors';
import type { Secret } from '../../types';
import { SECRET_LIST_ITEM_SIZES } from './constants';
import { ItemRenderer } from './ItemRenderer';

const DISPLAYED_PROPERTIES = [
    Fields.USERNAME,
    Fields.PASSWORD,
    Fields.HAS_TOTP,
    Fields.REDACTED_CARD_NUMBER,
    Fields.EXPIRATION_DATE,
    Fields.CARDHOLDER_NAME,
    Fields.CVV_CODE,
    Fields.PUBLIC_KEY,
    Fields.PRIVATE_KEY,
    Fields.CREDENTIALS,
    Fields.SECURE_NOTE,
] as const satisfies Array<KeysOfUnion<Secret>>;

// react-window rows are absolutely positioned against the nearest positioned
// ancestor — by default the outer scroll element, whose paddingTop would not
// displace them. Positioning the inner element re-anchors rows to it, so the
// outer padding pushes rows down while they stay visible scrolling through it.
const innerElementType = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(({ style, ...rest }, ref) => (
    <div ref={ref} style={{ ...style, position: 'relative' }} {...rest} />
));

export interface SecretsListProps {
    height: number;
    width: number;
    paddingTop?: number;
}

export const SecretsList = ({ height, width, paddingTop }: SecretsListProps) => {
    const secrets = useAppSelector(selectFilteredSecrets);

    const isMin768 = useBreakpoint('sm');
    const listRef = useRef<VariableSizeList<typeof secrets>>(null);

    const handleOffset = useSecretsListOffset(SecretListId.MAIN_LIST, listRef);

    const { getSharingIndicatorVisibility } = useSharingIndicatorVisibility();

    useEffect(() => {
        listRef.current?.resetAfterIndex(0, true);
    }, [isMin768, secrets]);

    return (
        <VariableSizeList<typeof secrets>
            ref={listRef}
            height={height}
            width={width}
            style={paddingTop ? { paddingTop } : undefined}
            innerElementType={innerElementType}
            itemData={secrets}
            itemCount={secrets.length}
            estimatedItemSize={
                isMin768
                    ? SECRET_LIST_ITEM_SIZES.base + SECRET_LIST_ITEM_SIZES.propertyRowSize
                    : SECRET_LIST_ITEM_SIZES.base + SECRET_LIST_ITEM_SIZES.propertyRowSize * 5
            }
            itemSize={index => {
                const item = secrets[index];
                const sensitiveFields = item.sensitiveFields ?? [];

                const displayedPropertiesLength = union(
                    Object.entries(pick(item, DISPLAYED_PROPERTIES))
                        .filter(([, value]) => (typeof value === 'string' && !isEmptyOrUndefined(value)) || value === true)
                        .map(([key]) => key as Fields),
                    intersection(sensitiveFields, DISPLAYED_PROPERTIES),
                ).length;

                const isSharingIndicatorVisible = getSharingIndicatorVisibility({
                    share: item.share,
                    userPermissions: item.userPermissions,
                });

                const hasTags = item.tags ? item.tags.length > 0 : false;

                let itemSize = SECRET_LIST_ITEM_SIZES.base;
                if (isMin768) return itemSize + SECRET_LIST_ITEM_SIZES.propertyRowSize;

                itemSize += displayedPropertiesLength * SECRET_LIST_ITEM_SIZES.propertyRowSize;
                if (isSharingIndicatorVisible) itemSize += SECRET_LIST_ITEM_SIZES.sharingIndicator;
                if (hasTags) itemSize += SECRET_LIST_ITEM_SIZES.tags;

                return itemSize;
            }}
            itemKey={index => {
                const item = secrets[index];
                return item.key;
            }}
            onScroll={({ scrollOffset }) => handleOffset(scrollOffset)}
        >
            {/* @ts-expect-error */}
            {ItemRenderer}
        </VariableSizeList>
    );
};
